import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import {
  getQuoteScoreLevelStyle,
  QUOTE_SCORE_MUTED_STYLE,
  QuoteScoreBreakdown,
} from "components/QuoteScore";
import { ClientAppEvent } from "constants/index";
import { useDisclosure } from "hooks";
import { isDeadQuote } from "utils";

import { getStickyActionsCellSx } from "../constants";

interface Props {
  quote: ShipperCondensedQuote;
  score: ShipperQuoteScore;
  hasHorizontalOverflow: boolean;
}

// Every column but the pinned ACTIONS one, which gets its own cell so it can
// stay pinned while the rest of the band scrolls.
const SCROLLING_COLUMN_COUNT = 6;

/**
 * A quote's competitiveness verdict, as a tinted band above its row, opening
 * into the three-factor evidence behind it
 * (docs/engineering/design/quote_competitiveness_scoring_design.md §14).
 */
export function QuoteScoreBand(props: Props) {
  const { quote, score, hasHorizontalOverflow } = props;
  const posthog = usePostHog();
  const { isOpen, onOpen, onClose } = useDisclosure(false);

  const overallLabel = score.overall_label;
  // No verdict when Price cannot be scored, i.e. the only quote on the load.
  if (overallLabel == null) {
    return null;
  }

  const levelStyle = getQuoteScoreLevelStyle(overallLabel);
  // A quote that can no longer be booked keeps its verdict but loses the
  // colour, so attention stays on quotes the shipper can still act on.
  const muted = isDeadQuote(quote.internal_state);
  const colourStyle = muted ? QUOTE_SCORE_MUTED_STYLE : levelStyle;

  const onToggle = () => {
    if (isOpen) {
      onClose();
      return;
    }
    posthog?.capture(
      ClientAppEvent.SHIPPER_QUOTE_COMPETITIVENESS_SCORE_PANEL_EXPANDED,
      {
        quote_id: quote.quote,
        shipment_id: quote.shipment,
        overall_label: overallLabel,
      },
    );
    onOpen();
  };

  const bandCellSx = {
    py: 0.75,
    borderBottom: "none",
    backgroundColor: colourStyle.tint,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  };
  const breakdownCellSx = {
    pt: 1.5,
    pb: 1,
    borderBottom: "none",
    backgroundColor: "background.paper",
  };
  const ceilingLabel = score.ceiling_label;

  return (
    <>
      <TableRow
        onClick={onToggle}
        sx={{ "&:hover > td": { backgroundColor: colourStyle.hoverTint } }}
      >
        <TableCell colSpan={SCROLLING_COLUMN_COUNT} sx={bandCellSx}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
              aria-hidden="true"
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: colourStyle.color,
              }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: colourStyle.color,
              }}
            >
              {levelStyle.label}
            </Typography>
            <Typography
              aria-hidden="true"
              sx={{ fontSize: "14px", color: colourStyle.color, opacity: 0.6 }}
            >
              ·
            </Typography>
            <Tooltip
              title={
                isOpen
                  ? "Hide the price, history, and profile evidence behind this score"
                  : "Show the price, history, and profile evidence behind this score"
              }
            >
              <Box
                component="button"
                type="button"
                aria-expanded={isOpen}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  p: 0,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: colourStyle.color,
                }}
              >
                {isOpen ? "Hide" : "See why"}
                {isOpen ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
            </Tooltip>
          </Stack>
        </TableCell>
        <TableCell
          sx={[bandCellSx, getStickyActionsCellSx(hasHorizontalOverflow, 2)]}
        />
      </TableRow>
      {isOpen && (
        <TableRow>
          <TableCell colSpan={SCROLLING_COLUMN_COUNT} sx={breakdownCellSx}>
            {ceilingLabel != null && (
              <Typography
                sx={{ fontSize: "13px", color: "text.secondary", mb: 1.5 }}
              >
                {`Price capped this to ${getQuoteScoreLevelStyle(ceilingLabel).label}. See Price below for why.`}
              </Typography>
            )}
            <QuoteScoreBreakdown
              result={score}
              audience="shipper"
              muted={muted}
            />
          </TableCell>
          <TableCell
            sx={[
              breakdownCellSx,
              getStickyActionsCellSx(hasHorizontalOverflow, 2),
            ]}
          />
        </TableRow>
      )}
    </>
  );
}
